import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Loader2, Eye, EyeOff, UserPlus, LogIn } from 'lucide-react';
import { adminLogin, adminRegister } from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_josmart-staging/artifacts/r0wcvy09_file_00000000fee871f598adf429b9fe9c58.png";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    adminCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      let response;
      if (isRegister) {
        response = await adminRegister(formData.email, formData.password, formData.adminCode);
      } else {
        response = await adminLogin(formData.email, formData.password);
      }
      
      localStorage.setItem('adminToken', response.access_token);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="JoSmart" 
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-[#1E3A5F]">
              {isRegister ? 'Créer un compte admin' : 'Connexion Admin'}
            </h1>
            <p className="text-gray-500 mt-2">
              {isRegister ? 'Inscrivez-vous pour accéder au dashboard' : 'Connectez-vous pour accéder au dashboard'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] focus:border-transparent outline-none transition-colors bg-white text-gray-900"
                  placeholder="admin@josmart.com"
                  data-testid="admin-input-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] focus:border-transparent outline-none transition-colors bg-white text-gray-900"
                  placeholder="••••••••"
                  data-testid="admin-input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {isRegister && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Code d'inscription</label>
                <input
                  type="text"
                  name="adminCode"
                  value={formData.adminCode}
                  onChange={handleChange}
                  required={isRegister}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90D9] focus:border-transparent outline-none transition-colors bg-white text-gray-900"
                  placeholder="Code fourni par JoSmart"
                  data-testid="admin-input-code"
                />
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="admin-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-[#4A90D9] text-white rounded-lg font-semibold hover:bg-[#5BA0E9] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="admin-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  {isRegister ? 'Inscription...' : 'Connexion...'}
                </>
              ) : (
                <>
                  {isRegister ? <UserPlus size={20} /> : <LogIn size={20} />}
                  {isRegister ? 'S\'inscrire' : 'Se connecter'}
                </>
              )}
            </button>
          </form>

          {/* Toggle */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsRegister(!isRegister);
                setError('');
              }}
              className="text-[#4A90D9] hover:underline text-sm"
              data-testid="admin-toggle-mode"
            >
              {isRegister ? 'Déjà un compte ? Se connecter' : 'Pas de compte ? S\'inscrire'}
            </button>
          </div>

          {/* Back to site */}
          <div className="mt-4 text-center">
            <a href="/" className="text-gray-500 hover:text-gray-700 text-sm">
              ← Retour au site
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
